import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Phone, KeyRound, ArrowRight, ShieldCheck } from "lucide-react";

export default function Auth() {
  const { otpRequest, otpVerify, login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";
  const go = () => nav(redirect);
  const [mode, setMode] = useState("phone");      // phone | email
  const [step, setStep] = useState("phone");        // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const boxes = useRef([]);

  useEffect(() => { if (resendIn <= 0) return; const t = setTimeout(() => setResendIn((s) => s - 1), 1000); return () => clearTimeout(t); }, [resendIn]);

  const cleanPhone = phone.replace(/\D/g, "");
  const sendOtp = async () => {
    if (cleanPhone.length < 10) return toast.error("Enter a valid phone number");
    setLoading(true);
    try {
      const r = await otpRequest(cleanPhone);
      setDevOtp(r.mock_otp || "");
      setStep("otp"); setResendIn(30);
      setTimeout(() => boxes.current[0]?.focus(), 100);
      toast.success("OTP sent");
    } catch { toast.error("Could not send OTP"); }
    setLoading(false);
  };

  const setDigit = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[i] = d; setOtp(next);
    if (d && i < 5) boxes.current[i + 1]?.focus();
  };
  const onKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) boxes.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (t.length) { setOtp(t.padEnd(6, "").split("").slice(0, 6).map((c) => c || "")); boxes.current[Math.min(t.length, 5)]?.focus(); e.preventDefault(); }
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length < 6) return toast.error("Enter the 6-digit OTP");
    setLoading(true);
    try { await otpVerify({ phone: cleanPhone, otp: code }); toast.success("Signed in"); go(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
    setLoading(false);
  };

  const emailLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success("Welcome back"); go(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };

  return (
    <main className="min-h-[85vh] grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      {/* Visual side */}
      <div className="relative hidden lg:block bg-black">
        <img src="https://images.unsplash.com/photo-1591944489410-16ec1074a18e?crop=entropy&cs=srgb&fm=jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="sq-eyebrow text-white/70">Your Atelier Account</div>
          <h1 className="font-display text-5xl mt-4 leading-[0.95]">Step inside the cutting room.</h1>
          <p className="mt-4 text-white/70 max-w-md">Sign in with your phone to book bespoke, track orders, and save your measurements.</p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-10 md:p-16">
        <div className="w-full max-w-sm">
          <div className="sq-eyebrow text-squire-jeans">SquireCouture</div>

          {mode === "phone" ? (
            step === "phone" ? (
              <>
                <h2 className="font-display text-4xl mt-2">Sign in / Sign up</h2>
                <p className="text-sm text-[#4B5563] mt-2">Enter your phone number — we'll send a 6-digit code.</p>
                <form onSubmit={(e) => { e.preventDefault(); sendOtp(); }} className="mt-8">
                  <label className="sq-eyebrow text-[10px]">Phone Number</label>
                  <div className="mt-2 flex items-center gap-2 border border-black/20 rounded-lg px-3 py-3 focus-within:border-squire-jeans">
                    <Phone size={16} className="text-[#9CA3AF]" />
                    <span className="text-sm text-[#4B5563]">+91</span>
                    <input data-testid="auth-phone" autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" placeholder="98765 43210" className="flex-1 bg-transparent outline-none text-sm" />
                  </div>
                  <button type="submit" data-testid="auth-send-otp" disabled={loading} className="sq-btn-primary w-full mt-6 justify-center">
                    {loading ? "Sending…" : "Send OTP"} <ArrowRight size={16} />
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="font-display text-4xl mt-2">Verify OTP</h2>
                <p className="text-sm text-[#4B5563] mt-2">Sent to +91 {phone}. <button onClick={() => { setStep("phone"); setOtp(["", "", "", "", "", ""]); }} className="text-squire-jeans underline">Change</button></p>
                {devOtp && <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="auth-dev-otp">Demo OTP: <strong>{devOtp}</strong> (auto-generated for testing)</div>}
                <div className="mt-6 flex gap-2 justify-between" onPaste={onPaste}>
                  {otp.map((d, i) => (
                    <input key={i} ref={(el) => (boxes.current[i] = el)} data-testid={`auth-otp-${i}`} value={d}
                      onChange={(e) => setDigit(i, e.target.value)} onKeyDown={(e) => onKey(i, e)} inputMode="numeric" maxLength={1}
                      className="w-12 h-14 text-center text-xl font-display border border-black/20 rounded-lg outline-none focus:border-squire-jeans" />
                  ))}
                </div>
                <button data-testid="auth-verify-otp" onClick={verify} disabled={loading} className="sq-btn-primary w-full mt-6 justify-center">
                  {loading ? "Verifying…" : "Verify & Continue"}
                </button>
                <div className="text-center mt-4 text-sm text-[#4B5563]">
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : <button onClick={sendOtp} className="text-squire-jeans underline">Resend OTP</button>}
                </div>
              </>
            )
          ) : (
            <>
              <h2 className="font-display text-4xl mt-2">Staff Sign In</h2>
              <p className="text-sm text-[#4B5563] mt-2">Admin / manager access with email and password.</p>
              <form onSubmit={emailLogin} className="mt-8 space-y-4">
                <div>
                  <label className="sq-eyebrow text-[10px]">Email</label>
                  <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-2 border border-black/20 rounded-lg px-3 py-3 text-sm outline-none focus:border-squire-jeans" />
                </div>
                <div>
                  <label className="sq-eyebrow text-[10px]">Password</label>
                  <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mt-2 border border-black/20 rounded-lg px-3 py-3 text-sm outline-none focus:border-squire-jeans" />
                </div>
                <button type="submit" data-testid="auth-email-submit" disabled={loading} className="sq-btn-primary w-full justify-center">{loading ? "Please wait…" : "Sign In"}</button>
              </form>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-black/10 text-center">
            <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "phone" ? "email" : "phone"); setStep("phone"); }} className="text-xs text-[#4B5563] hover:text-black">
              {mode === "phone" ? "Staff / admin? Sign in with email →" : "← Customer? Sign in with phone"}
            </button>
          </div>
          <p className="text-[11px] text-[#9CA3AF] flex items-center justify-center gap-1.5 mt-4"><ShieldCheck size={12} /> OTP is auto-generated in test mode. Plug in an SMS provider to go live.</p>
        </div>
      </div>
    </main>
  );
}
